import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  View,
  TouchableOpacity,
  Pressable,
  ScrollView,
  StyleSheet,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useRole,
  useDismiss,
  useInteractions,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react";
import Colors from "../../Resources/Color";

/**
 * FloatingMenu
 *
 * Purpose
 * - Generic, reusable floating context/menu popover for RN-web.
 * - Anchors to a trigger element and renders a scrollable list of items in a portal
 *   using @floating-ui positioning (offset/flip/shift + autoUpdate).
 * - Supports keyboard navigation (ArrowUp/ArrowDown, Enter/Space) and pointer hover.
 *
 * How it works (high-level)
 * - `trigger` is rendered inside a TouchableOpacity; clicking/pressing toggles `isOpen`.
 * - When open, the menu is rendered inside a FloatingPortal and positioned via
 *   `useFloating()` relative to the trigger.
 * - Each menu row is a Pressable that hosts your custom `renderItem()` output.
 * - A small fade gradient is drawn at the top/bottom to hint scrollability.
 *
 * Props
 * @param {React.ReactNode} trigger
 *   Visual element that opens/closes the menu (e.g., icon button, pill, etc.).
 *
 * @param {Array<any>} [items=[]]
 *   Data backing the menu rows. This component does not interpret the items; it just
 *   maps them and passes each to `renderItem`.
 *
 * @param {(args: { item: any; index: number; close: () => void }) => React.ReactNode} renderItem
 *   Row renderer. Receives:
 *   - `item`: the corresponding element from `items`
 *   - `index`: the row index
 *   - `close()`: helper to close the menu after an action
 *
 * @param {number} [width=220]
 *   Menu container width.
 *
 * @param {number} [maxHeight=300]
 *   Menu container maximum height; content becomes scrollable when exceeded.
 *
 * @param {object} [menuStyle={}]
 *   Optional style overrides merged into the menu container style.
 *
 * @param {import('@floating-ui/react').Placement} [placement="bottom-start"]
 *   Floating-ui placement relative to the trigger.
 *
 * @param {(event?: any) => void} [onTriggerPress]
 *   Optional callback invoked when the trigger is pressed/clicked (before toggling).
 *
 * Usage
 * ```jsx
 * <FloatingMenu
 *   trigger={<MyIconButton />}
 *   items={[{ label: "Edit" }, { label: "Delete" }]}
 *   renderItem={({ item, close }) => (
 *     <TouchableOpacity
 *       onPress={() => {
 *         doSomething(item);
 *         close();
 *       }}
 *     >
 *       <Text>{item.label}</Text>
 *     </TouchableOpacity>
 *   )}
 * />
 * ```
 */
const FloatingMenu = ({
  trigger,
  items = [],
  renderItem,
  width = 220,
  maxHeight = 300,
  menuStyle = {},
  placement = "bottom-start",
  onTriggerPress,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [interactionMode, setInteractionMode] = useState("keyboard");

  const itemContainerRefs = useRef([]);
  const scrollViewRef = useRef(null);
  const keyboardNavigationRef = useRef(false);

  const handleMouseMove = useCallback(() => {
    keyboardNavigationRef.current = false;
  }, []);

  const getScrollableNode = useCallback(() => {
    const view = scrollViewRef.current;
    if (!view) return null;
    if (typeof view.getScrollableNode === "function") {
      const node = view.getScrollableNode();
      if (node) return node;
    }
    return view;
  }, []);

  const scrollToTop = useCallback(() => {
    const view = scrollViewRef.current;
    const node = getScrollableNode();
    if (!view || !node) return;

    try {
      const clampTop = () => {
        if (typeof node.scrollTo === "function") node.scrollTo({ top: 0 });
        node.scrollTop = 0;
      };

      if (typeof view.scrollTo === "function") {
        view.scrollTo({ y: 0, animated: false });
      } else if (typeof node.scrollTo === "function") {
        node.scrollTo({ top: 0, behavior: "auto" });
      }

      clampTop();
      requestAnimationFrame(clampTop);
    } catch {
      // no-op
    }
  }, [getScrollableNode]);

  const scrollToBottom = useCallback(() => {
    const view = scrollViewRef.current;
    const node = getScrollableNode();
    if (!view || !node) return;

    try {
      const target = Math.max(
        (node.scrollHeight || 0) - (node.clientHeight || 0),
        0
      );

      const clampBottom = () => {
        if (typeof node.scrollTo === "function") node.scrollTo({ top: target });
        node.scrollTop = target;
      };

      if (typeof view.scrollToEnd === "function") {
        view.scrollToEnd({ animated: false });
      } else if (typeof view.scrollTo === "function") {
        view.scrollTo({ y: target, animated: false });
      } else if (typeof node.scrollTo === "function") {
        node.scrollTo({ top: target, behavior: "auto" });
      }

      clampBottom();
      requestAnimationFrame(clampBottom);
    } catch {
      // no-op
    }
  }, [getScrollableNode]);

  const focusItem = useCallback((index) => {
    const container = itemContainerRefs.current[index];
    if (!container) return;

    const focusable =
      container.querySelector?.(
        'button,[role="menuitem"],[role="button"],[tabindex]:not([tabindex="-1"]),a[href],input,select,textarea'
      ) ?? null;

    if (focusable && typeof focusable.focus === "function") {
      // Avoid the default focus ring getting clipped by overflow hidden.
      try {
        focusable.style.outline = "none";
        focusable.style.boxShadow = "none";
      } catch {
        // no-op
      }
      focusable.focus();
      return;
    }

    if (typeof container.focus === "function") container.focus();
  }, []);

  const clickActiveItem = useCallback(() => {
    if (activeIndex === null) return;
    const container = itemContainerRefs.current[activeIndex];
    if (!container) return;

    const clickable =
      container.querySelector?.(
        'button,[role="button"],a[href],[tabindex]:not([tabindex="-1"])'
      ) ?? null;

    if (!clickable) return;

    if (typeof clickable.click === "function") {
      clickable.click();
      return;
    }

    try {
      clickable.dispatchEvent?.(
        new MouseEvent("click", { bubbles: true, cancelable: true })
      );
    } catch {
      // no-op
    }
  }, [activeIndex]);

  const handleMenuKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;
      if (!items || items.length === 0) return;

      if (e.key === "Enter" || e.key === " ") {
        // If the focused element already handles Enter/Space, let it.
        const activeEl =
          typeof document !== "undefined" ? document.activeElement : null;

        const activeContainer =
          activeIndex !== null ? itemContainerRefs.current[activeIndex] : null;

        const isInsideActive =
          activeEl && activeContainer && activeContainer.contains?.(activeEl);

        const isNaturallyClickable =
          activeEl &&
          (activeEl.tagName === "BUTTON" ||
            activeEl.tagName === "A" ||
            activeEl.getAttribute?.("role") === "button");

        setInteractionMode("keyboard");
        if (!(isInsideActive && isNaturallyClickable)) {
          e.preventDefault();
          clickActiveItem();
        }
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        keyboardNavigationRef.current = true;
        setInteractionMode("keyboard");
        let nextIndex = 0;

        if (activeIndex === null) {
          nextIndex = 0;
        } else if (activeIndex === items.length - 1) {
          nextIndex = 0;
        } else {
          nextIndex = activeIndex + 1;
        }

        if (nextIndex === 0) scrollToTop();
        if (nextIndex === items.length - 1) scrollToBottom();
        setActiveIndex(nextIndex);
        requestAnimationFrame(() => focusItem(nextIndex));
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        keyboardNavigationRef.current = true;
        setInteractionMode("keyboard");
        let nextIndex = items.length - 1;

        if (activeIndex === null) {
          nextIndex = items.length - 1;
        } else if (activeIndex === 0) {
          nextIndex = items.length - 1;
        } else {
          nextIndex = activeIndex - 1;
        }

        if (nextIndex === 0) scrollToTop();
        if (nextIndex === items.length - 1) scrollToBottom();
        setActiveIndex(nextIndex);
        requestAnimationFrame(() => focusItem(nextIndex));
      }
    },
    [
      activeIndex,
      clickActiveItem,
      focusItem,
      isOpen,
      items,
      scrollToBottom,
      scrollToTop,
    ]
  );

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset(6),
      flip({ fallbackAxisSideDirection: "start" }),
      shift({ padding: 10 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (!isOpen) return;
    if (typeof window === "undefined") return;

    const onKeyDown = (e) => {
      const floatingEl = refs.floating?.current;
      const activeEl =
        typeof document !== "undefined" ? document.activeElement : null;
      if (!floatingEl || !activeEl) return;
      if (!floatingEl.contains(activeEl)) return;

      handleMenuKeyDown(e);
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [handleMenuKeyDown, isOpen, refs]);

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "menu" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
  ]);

  const toggleOpen = (e) => {
    // RN-web: prevent nested triggers from also firing parent onPress/onClick.
    e?.stopPropagation?.();

    try {
      onTriggerPress?.(e);
    } catch {
      // no-op
    }

    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(null);
      return;
    }

    setInteractionMode("keyboard");
    setActiveIndex(0);
    const raf = requestAnimationFrame(() => focusItem(0));
    return () => cancelAnimationFrame(raf);
  }, [focusItem, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (activeIndex === null) return;
    if (items.length === 0) return;
    if (!keyboardNavigationRef.current) return;

    if (activeIndex === 0) {
      scrollToTop();
    } else if (activeIndex === items.length - 1) {
      scrollToBottom();
    }
  }, [activeIndex, isOpen, items.length, scrollToBottom, scrollToTop]);

  return (
    <>
      <TouchableOpacity
        ref={refs.setReference}
        style={styles.triggerContainer}
        onPress={toggleOpen}
        // RN-web: ensure mouse users also open the menu.
        onClick={toggleOpen}
        {...getReferenceProps()}
        activeOpacity={0.7}
      >
        {trigger}
      </TouchableOpacity>

      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <View
              ref={refs.setFloating}
              style={[
                styles.menuContainer,
                floatingStyles,
                { width, maxHeight },
                menuStyle,
              ]}
              {...getFloatingProps()}
            >
              <View style={styles.scrollRegion}>
                <ScrollView
                  ref={scrollViewRef}
                  style={styles.scrollView}
                  contentContainerStyle={styles.scrollContent}
                  keyboardShouldPersistTaps="handled"
                  showsVerticalScrollIndicator={true}
                  onMouseMove={handleMouseMove}
                >
                  {items.map((item, index) => (
                    <Pressable
                      key={index}
                      accessibilityRole="menuitem"
                      tabIndex={-1}
                      ref={(node) => {
                        itemContainerRefs.current[index] = node;
                      }}
                      onHoverIn={() => {
                        setInteractionMode("pointer");
                        if (!keyboardNavigationRef.current) {
                          setActiveIndex(index);
                        }
                      }}
                      style={({ hovered, pressed }) => [
                        styles.menuItemContainer,
                        interactionMode === "pointer" &&
                          hovered &&
                          styles.menuItemHovered,
                        activeIndex === index && styles.menuItemActive,
                        pressed && styles.menuItemPressed,
                      ]}
                    >
                      {renderItem({
                        item,
                        index,
                        close: () => setIsOpen(false),
                      })}
                    </Pressable>
                  ))}
                </ScrollView>
                <LinearGradient
                  pointerEvents="none"
                  colors={[Colors.whiteColor, "transparent"]}
                  style={styles.topFade}
                />

                <LinearGradient
                  pointerEvents="none"
                  colors={["transparent", Colors.whiteColor]}
                  style={styles.bottomFade}
                />
              </View>
            </View>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  triggerContainer: {
    // Let the trigger determine its own size, but don't override the parent's
    // cross-axis alignment (e.g., pills want the trigger vertically centered).
    alignSelf: "auto",
    alignItems: "center",
    justifyContent: "center",
  },
  menuContainer: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    paddingVertical: 0,
    zIndex: 99999, // Ensure it's above other elements
    boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
    outline: "none",
  },
  scrollRegion: {
    flexGrow: 1,
    flexShrink: 1,
    position: "relative",
    borderRadius: 12,
    overflow: "hidden",
  },
  scrollView: {
    flexGrow: 1,
  },
  scrollContent: {
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  menuItemContainer: {
    width: "100%",
    borderRadius: 10,
    overflow: "hidden",
    outlineStyle: "none",
    outlineWidth: 0,
  },
  menuItemHovered: {
    backgroundColor: Colors.hoveredGray,
    cursor: "pointer",
  },
  menuItemActive: {
    backgroundColor: Colors.hoveredGray,
  },
  menuItemPressed: {
    backgroundColor: Colors.hoveredGray,
  },
  topFade: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 18,
  },
  bottomFade: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 18,
  },
});

export default FloatingMenu;
